import { z } from 'zod';
/**
 * Hợp đồng cho **mọi** ước lượng do AI đưa ra — APM-AI-y
 * (`plan-ai-features.md` §0.4).
 *
 * Áp cho mọi skill trả ra một con số về khối lượng, thời gian, chi phí hay xác
 * suất. Không có ngoại lệ "chỉ là con số nhỏ": một con số không kèm căn cứ thì
 * người duyệt không có gì để cãi lại, và thứ duy nhất họ làm được là tin hoặc
 * bỏ qua — cả hai đều không phải "duyệt".
 *
 * ## Ba luật, cưỡng chế ở harness chứ không ở prompt
 *
 * 1. `evidence` **tối thiểu một mục**, và mọi `ref` phải có trong kết quả tool
 *    của lượt chạy (grounding check, APM-H-n). Model không viết được "dựa trên
 *    kinh nghiệm" mà không trỏ tới gì.
 * 2. Ước lượng theo tương đồng là bước **đầu bắt buộc**: `find_similar_tasks`
 *    có trả kết quả thì phải có ít nhất một dẫn chứng `similar_task` mang trọng
 *    số `primary`.
 * 3. `confidence` **không do model tự phong** — `capConfidence` ở
 *    `@apm/estimate` hạ nó theo số dẫn chứng và độ phân tán của tỉ lệ
 *    thực/ước lượng. Model đề nghị, hệ thống chốt.
 */
export const EstimateEvidenceKind = z.enum([
    'similar_task',
    'velocity',
    'calendar',
    'dependency',
    'assumption',
    'knowledge',
]);
export const EstimateEvidence = z.object({
    kind: EstimateEvidenceKind,
    /**
     * Định danh của thứ được trỏ tới: `taskId`, `userId`, id mục sổ kiến thức.
     *
     * Bỏ trống **chỉ** với `assumption` — một giả định theo định nghĩa là thứ
     * chưa có trong dữ liệu. Mọi loại còn lại có `ref` thì grounding check đòi nó
     * xuất hiện trong kết quả tool, nên một `ref` bịa ra làm hỏng cả lượt chạy
     * thay vì lặng lẽ đi qua.
     */
    ref: z.string().max(100).optional(),
    /** Một dòng cho người duyệt đọc: "APM-142 ước 5 điểm, thực tế 32 giờ". */
    summary: z.string().min(1).max(200),
    weight: z.enum(['primary', 'supporting']),
});
export const EstimateUnit = z.enum(['sp', 'hours', 'days', 'usd', 'pct']);
export const EstimateConfidence = z.enum(['low', 'medium', 'high']);
export const Estimate = z.object({
    value: z.number(),
    unit: EstimateUnit,
    /** Khoảng, khi nói được. Một con số trần đọc như một lời hứa. */
    range: z.object({ low: z.number(), high: z.number() }).optional(),
    confidence: EstimateConfidence,
    /** Vì sao ra con số này — ngắn, viết cho người duyệt chứ không cho log. */
    rationale: z.string().min(1).max(600),
    evidence: z.array(EstimateEvidence).min(1).max(10),
    /**
     * Vì sao `confidence` bị hạ so với mức model đề nghị — do `capConfidence`
     * điền, không phải do model.
     *
     * Hiện ngay cạnh mức tin cậy: "hạ xuống medium vì chỉ có 2 việc tương tự" nói
     * với người duyệt nhiều hơn một chữ `medium` đứng một mình.
     */
    confidenceNote: z.string().max(200).nullable().optional(),
});
//# sourceMappingURL=estimate.js.map